import { GraphSchema } from '@ta/server';

import { BaseDto } from '../../baseDto';

export interface NotificationMetadataKey {
  estateId?: string;
  estateName?: string;
  tenantApplicationId?: string;
  tenantId?: string;
  subscriptionId?: string;
  reviewId?: string;
}

export interface NotificationMetadata {
  key?: string;
  value?: string;
}

/** Toutes les clés que le serveur émet (`NotificationKeys.cs`). Les dates des métadonnées sont en UTC. */
export type NotificationKey =
  // Reviews
  | 'review.owner.received' // Metadata: TenantApplicationId, ReviewId
  | 'review.tenant.received' // Metadata: TenantApplicationId, TenantId, ReviewId
  | 'review.estate.received' // Metadata: TenantApplicationId, EstateId, ReviewId
  // Applications. Metadata: TenantApplicationId, EstateId, EstateName
  | 'application.tenant.invited'
  | 'application.owner.new'
  | 'application.tenant.owner_approved'
  | 'application.tenant.owner_rejected'
  | 'application.owner.tenant_approved'
  | 'application.owner.tenant_rejected'
  | 'application.accepted'
  | 'application.rejected'
  | 'application.other_accepted'
  | 'application.done'
  | 'application.tenant.added'
  | 'application.tenant.removed'
  // Estates. Metadata: EstateId
  | 'estate.validated'
  | 'estate.rejected'
  | 'estate.approved' // plus émise, anciennes lignes
  // Subscriptions. Metadata: EstateId, EstateName, SubscriptionId
  | 'subscription.activated'
  | 'subscription.canceled'
  | 'subscription.payment_failed'
  | 'subscription.renewed'
  | 'subscription.resumed'
  | 'subscription.interval_changed'
  | 'subscription.expired'
  | 'subscription.estate_hidden'
  | 'subscription.payment_method_updated'
  | 'subscription.extended' // plus émise
  | 'subscription.grace_ending' // plus émise
  // Premier bien offert : à chaque bailleur du bien. FreeAccessEndAt en UTC (yyyy-MM-ddTHH:mm:ssZ).
  | 'subscription.first_estate_free_granted' // Metadata: EstateId, EstateName, FreeAccessEndAt
  | 'subscription.grant_expiring' // Metadata: EstateId, EstateName, FreeAccessEndAt
  | 'subscription.grant_expired' // Metadata: EstateId, EstateName
  // Boosts. Metadata: EstateId, EstateName (+ BoostEndAt)
  | 'boost.activated'
  | 'boost.payment_failed'
  | 'boost.expiring'
  | 'boost.expired'
  // Suggestions
  | 'suggestion.received' // Metadata: EstateId, EstateName, SuggestionId
  // Invitations. Metadata: InvitationId, InvitedEmail
  | 'invitation.sent'
  | 'invitation.accepted'
  // Sociétés. Metadata: CompanyId, CompanyName (+ CompanyRole, EstateId, EstateName)
  | 'company.member_added'
  | 'company.member_removed'
  | 'company.role_changed'
  | 'company.estate_assigned'
  | 'company.estate_unassigned'
  // Visites. Metadata: VisitBookingId, EstateId, EstateName, VisitStartAt (+ texte libre)
  | 'visit.booked'
  | 'visit.confirmed'
  | 'visit.declined'
  | 'visit.cancelled_by_tenant'
  | 'visit.cancelled_by_landlord'
  | 'visit.no_show'
  // Favoris : au plus une par (favori, clé) par 24 h ; deux clés peuvent tomber ensemble sur un même bien.
  | 'favorite.estate_unavailable' // Le bien est loué ou supprimé. Metadata: EstateId, EstateName
  | 'favorite.estate_available' // Le bien est de nouveau proposé. Metadata: EstateId, EstateName
  | 'favorite.rent_decreased' // Loyer + charges en baisse. Metadata: EstateId, EstateName, OldMonthlyCost, NewMonthlyCost (entiers en chaîne, euros)
  // Identity
  | 'identity.verified'; // Metadata: VerificationId

export const getNotificationExtractValueOfKey = (key: string, metadata: NotificationMetadata[]) =>
  metadata.find((data) => data.key?.toLocaleLowerCase() === key.toLocaleLowerCase())?.value;

export type NotificationKeyType =
  | 'review'
  | 'application'
  | 'subscription'
  | 'invitation'
  | 'identity'
  | 'boost'
  | 'suggestion'
  | 'favorite'
  | 'estate'
  | 'company'
  | 'visit';

export type NotificationKeySubType =
  | 'owner'
  | 'tenant'
  | 'estate'
  | 'accepted'
  | 'rejected'
  | 'other_accepted'
  | 'done'
  | 'activated'
  | 'canceled'
  | 'sent'
  | 'verified'
  | 'received'
  | 'estate_unavailable'
  | 'estate_available'
  | 'rent_decreased'
  | 'validated'
  | 'payment_failed'
  | 'expiring'
  | 'expired'
  | 'member_added'
  | 'member_removed'
  | 'role_changed'
  | 'estate_assigned'
  | 'estate_unassigned'
  | 'booked'
  | 'confirmed'
  | 'declined'
  | 'cancelled_by_tenant'
  | 'cancelled_by_landlord'
  | 'no_show';

export const getNotificationKeyType = (key: string): NotificationKeyType =>
  key.split('.')[0] as NotificationKeyType;

export const getNotificationKeySubType = (key: string): NotificationKeySubType =>
  key.split('.')[1] as NotificationKeySubType;

export interface Notification extends BaseDto {
  id: string;
  key: NotificationKey;
  isRead: boolean;
  userId: string;
  metadata: NotificationMetadata[];
}

export const notificationBrutProps: (keyof Notification)[] = [
  'id',
  'key',
  'isRead',
  'userId',
  'metadata',
  'createdDate',
];

export const notificationProps = new GraphSchema<Notification>(notificationBrutProps);

export const notificationMetadataPropsComposition = `
${notificationProps.get('metadata')}
   {
    key,
    value
  }
`;
